import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useStore } from '@nanostores/react';

import { ProfileProvider } from './context/ProfileContext';
import { MBAProfileProvider } from './context/MBAProfileContext';
import { $initialData } from './store/initial-data';
import InitialDataBootstrapper from './app/bootstrap/InitialDataBootstrapper';
import AppLayout from './app/layouts/AppLayout';
import AppRoutes from './app/routing/AppRoutes';
import LoadingScreen from './app/screens/LoadingScreen';
import { RequestCallbackProvider } from './app/context/RequestCallbackContext';
import useGTMSectionTracking from './hooks/useGTMSectionTracking';
import AuthSplitPage from './components/auth/AuthSplitPage';
import MBALandingPage from './components/mba-landing/MBALandingPage';
import MBAQuiz from './components/quiz/MBAQuiz';
import MBAResultsPage from './components/MBAResultsPage';
import MBAAdminViewPage from './components/admin/MBAAdminViewPage';
import MicrosoftClarity from './components/analytics/MicrosoftClarity';
import tracker from './utils/tracker';
import { initTracking } from './utils/tracking';
import { getPathWithQueryParams } from './utils/url';
import { apiRequest } from './utils/api';
import { addMeta } from './utils/dom';

import '@vectord/ui/dist/style.css';
import '@vectord/fp-styles';

function AuthRoutes() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const requestedRedirect = searchParams.get('redirect');
  const redirectPath = requestedRedirect || `${location.pathname}${location.search}`;
  const loginPath = `/login?redirect=${encodeURIComponent(redirectPath)}`;

  return (
    <Routes>
      <Route path="/login" element={<AuthSplitPage initialMode="login" />} />
      <Route path="/signup" element={<AuthSplitPage initialMode="signup" />} />
      <Route path="*" element={<Navigate to={loginPath} replace />} />
    </Routes>
  );
}

// MBA App Content - separate from main app with isolated context
function MBAAppContent() {
  const [quizProgress, setQuizProgress] = useState(0);
  const { data, loading } = useStore($initialData);
  const location = useLocation();
  useGTMSectionTracking();
  const isMBAAdminRoute = location.pathname.startsWith('/business-and-ai-readiness/admin');
  
  // Hide nav for MBA quiz page
  const shouldShowNav = !location.pathname.includes('/business-and-ai-readiness/quiz') && 
                        !location.pathname.startsWith('/admin');

  const navigationProps = useMemo(
    () => ({
      progress: quizProgress,
      quizMode: 'mba',
      onQuizModeChange: () => {}
    }),
    [quizProgress]
  );

  if (loading) return <LoadingScreen />;
  if (!data?.isLoggedIn && !isMBAAdminRoute) return <AuthRoutes />;

  return (
    <MBAProfileProvider>
      <AppLayout showNavigation={shouldShowNav} navigationProps={navigationProps}>
        <Routes>
          <Route
            path="/business-and-ai-readiness"
            element={<Navigate to={getPathWithQueryParams('/business-and-ai-readiness/quiz')} replace />}
          />
          <Route
            path="/business-and-ai-readiness/quiz"
            element={<MBAQuiz onProgressChange={setQuizProgress} />}
          />
          <Route path="/business-and-ai-readiness/mba-results" element={<MBAResultsPage />} />
          <Route 
            path="/business-and-ai-readiness/admin/view/response/:response_id" 
            element={<MBAAdminViewPage />} 
          />
        </Routes>
      </AppLayout>
    </MBAProfileProvider>
  );
}

function AppContent() {
  const [quizProgress, setQuizProgress] = useState(0);
  const [quizMode, setQuizMode] = useState('final');
  const { data, loading } = useStore($initialData);
  const location = useLocation();
  useGTMSectionTracking();
  
  // Check if we're on an MBA route
  const isMBARoute = location.pathname.startsWith('/business-and-ai-readiness');
  
  const shouldShowNav = !(
    quizMode === 'final' && location.pathname === '/quiz'
  ) && !location.pathname.startsWith('/admin') && !isMBARoute;

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isMBAAdminRoute = location.pathname.startsWith('/business-and-ai-readiness/admin');
  const isAnyAdminRoute = isAdminRoute || isMBAAdminRoute;
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const isAuthOrDefaultQuizRoute = ['/login', '/signup', '/quiz'].includes(location.pathname);
  const hasSafeRedirect = Boolean(redirectParam?.startsWith('/'));

  useEffect(() => {
    const pageUrl = new URL(window.location.href);
    tracker.superAttributes = {
      attributes: {
        page_path: pageUrl.pathname,
        page_url: pageUrl.href,
        query_params: Object.fromEntries(pageUrl.searchParams.entries())
      }
    };
  }, [location.pathname]);

  const navigationProps = useMemo(
    () => ({
      progress: quizProgress,
      quizMode,
      onQuizModeChange: setQuizMode
    }),
    [quizProgress, quizMode]
  );

  useEffect(() => {
    if (window.clarity) {
      if (isMBARoute) {
        window.clarity('set', 'experiment', 'online_mba_cpe');
      } else if (!isAdminRoute) {
        window.clarity('set', 'experiment', 'career_profile_tool');
      }
    }
  }, []);

  if (loading) return <LoadingScreen />;

  if (data?.isLoggedIn && isAuthOrDefaultQuizRoute && hasSafeRedirect) {
    return <Navigate to={redirectParam} replace />;
  }

  if (!data?.isLoggedIn && !isAnyAdminRoute) {
    // MBA landing lives at the explicit /lp-business-and-ai-readiness slug;
    // every other unauthenticated path falls through to the legacy CPE auth flow.
    const isMBALandingPath = location.pathname === '/lp-business-and-ai-readiness'
      || location.pathname === '/lp-business-and-ai-readiness/';
    if (isMBALandingPath) {
      return (
        <div className="mba-cpe-theme">
          <MBALandingPage />
        </div>
      );
    }
    return (
      <div className="cpe-theme">
        <AuthRoutes />
      </div>
    );
  }

  // Route to MBA app for /business-and-ai-readiness/* paths
  if (isMBARoute) {
    return (
      <div className="mba-cpe-theme">
        <MBAAppContent />
      </div>
    );
  }

  return (
    <div className="cpe-theme">
      <AppLayout showNavigation={shouldShowNav} navigationProps={navigationProps}>
        <AppRoutes onQuizProgressChange={setQuizProgress} {...{ quizMode }} />
      </AppLayout>
    </div>
  );
}

// Pre-Router check — runs before any auth bootstrap fires. The landing page is
// for unauthenticated users; calling /generate-jwt or /api/v3/users from here
// triggers Scaler's auth middleware to redirect the browser to
// /career-profile-tool/login (the legacy CPE login). We render the landing
// directly and skip those auth calls — but we still need /csrf-token, because
// the OTP signup POST to /users/v2/ requires X-CSRF-Token. Without it, Scaler's
// Rails backend treats the request as a non-XHR form post and responds with a
// Turbolinks.visit redirect instead of dispatching an OTP.
function isStandaloneLandingPath() {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p === '/career-profile-evaluation/lp-business-and-ai-readiness'
    || p === '/career-profile-evaluation/lp-business-and-ai-readiness/';
}

async function fetchCsrfToken() {
  try {
    const { csrf_token: csrfToken } = (await apiRequest('GET', '/csrf-token')) || {};
    if (csrfToken) addMeta('csrf-token', csrfToken);
  } catch (err) {
    console.error('[Landing] CSRF token fetch failed:', err);
  }
}

// Best-effort logged-in probe for the standalone LP. We POST to /generate-jwt
// — the same call the auth bootstrap uses — and treat a non-empty 200 as
// "logged in." Fail-safe: any error, timeout, or unexpected response resolves
// false so the LP still renders. CSRF must be fetched before calling this.
async function isLoggedInOnLanding() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2000);
  try {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    const res = await fetch('/generate-jwt', {
      method: 'POST',
      credentials: 'include',
      redirect: 'manual',
      headers: {
        'Content-Type': 'text/plain',
        'X-Requested-With': 'XMLHttpRequest',
        ...(csrfMeta?.content ? { 'X-CSRF-Token': csrfMeta.content } : {})
      },
      body: JSON.stringify({}),
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!res.ok || res.status !== 200) return false;
    const token = await res.text();
    return Boolean(token && token.length > 0);
  } catch {
    clearTimeout(timeoutId);
    return false;
  }
}

// Matches LeadCard's post-OTP redirect — the quiz lives on the legacy CPE
// deployment under /career-profile-tool (different EB app, same scaler.com
// session cookie). Keep these two in sync if either path moves.
const LP_LOGGED_IN_REDIRECT = '/career-profile-tool/business-and-ai-readiness/quiz';

function App() {
  const [lpAuthChecked, setLpAuthChecked] = useState(false);

  useEffect(() => {
    initTracking();
  }, []);

  useEffect(() => {
    if (!isStandaloneLandingPath()) return;
    let cancelled = false;
    (async () => {
      // CSRF must come first — /generate-jwt requires it. The LP's OTP signup
      // flow also needs the token in the <meta> tag, so this fetch is
      // load-bearing regardless of whether the auth probe finds a session.
      await fetchCsrfToken();
      if (cancelled) return;
      const loggedIn = await isLoggedInOnLanding();
      if (cancelled) return;
      if (loggedIn) {
        window.location.assign(LP_LOGGED_IN_REDIRECT);
        return; // keep loading screen until navigation kicks in
      }
      setLpAuthChecked(true);
    })();
    return () => { cancelled = true; };
  }, []);

  if (isStandaloneLandingPath()) {
    if (!lpAuthChecked) return <LoadingScreen />;
    return (
      <>
        <MicrosoftClarity />
        <div className="mba-cpe-theme">
          <MBALandingPage />
        </div>
      </>
    );
  }

  return (
    <>
      <MicrosoftClarity />
      <ProfileProvider>
        <Router basename="/career-profile-evaluation">
          <RequestCallbackProvider>
            <InitialDataBootstrapper
              product="career_profile_tool"
              subProduct="free_evaluation"
            />
            <AppContent />
          </RequestCallbackProvider>
        </Router>
      </ProfileProvider>
    </>
  );
}

export default App;
